import React, { useState } from "react";
import { kcFetch, API_BASE } from "../../lib/apiClient";
import { useAppSettings, resolveLogo } from "../../lib/AppSettingsContext";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// VisitorWelcomePage — public welcome form (no auth)
// Mounted at /welcome via the public-route check in KingdomConnect.jsx.
// Submits to POST /api/visitors which appends a Guest member.
// ============================================================
export default function VisitorWelcomePage() {
  const { settings: appSettings, loaded: appSettingsLoaded } = useAppSettings();
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", email: "", dob: "", howDidYouHear: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setError("");
    if (!form.firstName.trim() || !form.lastName.trim() || !form.phone.trim() || !form.email.trim()) {
      setError("Please fill in your name, phone, and email so we can reach out.");
      return;
    }
    setBusy(true);
    try {
      const res = await kcFetch(`${API_BASE}/visitors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");
      setDone(true);
    } catch {
      setError("Something went wrong. Please try again or contact the church directly.");
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="visitor-bg">
      <div className="visitor-card" data-testid="visitor-success">
        {appSettingsLoaded
          ? <img src={resolveLogo(appSettings.appLogo)} alt={appSettings.appName} className="visitor-logo" />
          : <span aria-hidden="true" className="visitor-logo" style={{ visibility: "hidden" }} />}
        <div className="visitor-title">Welcome Home</div>
        <div className="visitor-tagline">{appSettings.appTagline}</div>
        <div style={{ textAlign: "center", marginTop: 28, fontFamily: "'Montserrat',sans-serif" }}>
          <div style={{ fontSize: 48, marginBottom: 14, display: "inline-flex" }}><CustomIcon name="praying-hands" size={54} /></div>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 18, color: "var(--kc-text)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Thank You, {form.firstName}!</div>
          <div style={{ fontSize: 14, color: "var(--kc-sub)", lineHeight: 1.7, maxWidth: 420, margin: "0 auto" }}>
            Your details have been received. A member of our welcome team will reach out to you shortly. We&apos;re so glad you connected with {appSettings.appName}.
          </div>
          <a href="/welcome" onClick={(e) => { e.preventDefault(); setDone(false); setForm({ firstName: "", lastName: "", phone: "", email: "", dob: "", howDidYouHear: "", message: "" }); }} style={{ display: "inline-block", marginTop: 24, fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "var(--kc-accent)", textDecoration: "underline" }}>Submit another visitor</a>
        </div>
      </div>
    </div>
  );

  return (
    <div className="visitor-bg">
      <div className="visitor-card">
        {appSettingsLoaded
          ? <img src={resolveLogo(appSettings.appLogo)} alt={appSettings.appName} className="visitor-logo" />
          : <span aria-hidden="true" className="visitor-logo" style={{ visibility: "hidden" }} />}
        <div className="visitor-title">Welcome</div>
        <div className="visitor-tagline">{appSettings.appTagline}</div>
        <p className="visitor-intro">We&apos;re honoured you&apos;ve joined us. Please share a few details so a team member can welcome you personally.</p>

        {error && <div className="login-error" data-testid="visitor-error">{error}</div>}

        <div className="form-grid">
          <div className="form-group">
            <label>First Name *</label>
            <input data-testid="visitor-firstName" value={form.firstName} onChange={e => upd("firstName", e.target.value)} placeholder="Your first name" />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input data-testid="visitor-lastName" value={form.lastName} onChange={e => upd("lastName", e.target.value)} placeholder="Your last name" />
          </div>
          <div className="form-group">
            <label>Phone Number *</label>
            <input data-testid="visitor-phone" value={form.phone} onChange={e => upd("phone", e.target.value)} placeholder="+1 555 000 0000" />
          </div>
          <div className="form-group">
            <label>Email Address *</label>
            <input data-testid="visitor-email" type="email" value={form.email} onChange={e => upd("email", e.target.value)} placeholder="you@email.com" />
          </div>
          <div className="form-group">
            <label>Date of Birth</label>
            <input data-testid="visitor-dob" type="date" value={form.dob} onChange={e => upd("dob", e.target.value)} />
          </div>
          <div className="form-group">
            <label>How did you hear about us?</label>
            <select data-testid="visitor-source" value={form.howDidYouHear} onChange={e => upd("howDidYouHear", e.target.value)}>
              <option value="">— Select —</option>
              <option>A friend or family member</option>
              <option>Walked past the church</option>
              <option>Social media</option>
              <option>Google / online search</option>
              <option>Community event</option>
              <option>Other</option>
            </select>
          </div>
          <div className="form-group full">
            <label>Anything you&apos;d like us to know? (optional)</label>
            <textarea data-testid="visitor-message" value={form.message} onChange={e => upd("message", e.target.value)} placeholder="Prayer requests, questions, or a short note..." />
          </div>
        </div>

        <button className="login-btn" data-testid="visitor-submit" onClick={submit} disabled={busy} style={{ marginTop: 22 }}>
          {busy ? "Submitting…" : "Send My Details"}
        </button>

        <div style={{ textAlign: "center", marginTop: 18 }}>
          <a href="/" style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "var(--kc-meta)", textDecoration: "underline" }}>← Back to staff sign-in</a>
        </div>
      </div>
    </div>
  );
}
